<template>
  <div>
    <slot name="export-button" :is-busy="isExporting" :sidebar-id="sidebarId">
      <b-button class="mr-3 custom-btn-secondary" :disabled="isBusy || itemsCount === 0" v-b-toggle="sidebarId">
        <b-spinner v-if="isBusy || isExporting" small/>
        <v-icon name="buttons/export" :size="12"/>
        Экспорт
      </b-button>
    </slot>

    <portal to="app">
      <b-sidebar :id="sidebarId" sidebar-class="custom-sidebar-container" title="Экспорт" right shadow backdrop>
        <div class="d-flex flex-column h-100">
          <div class="flex-grow-1 overflow-auto p-3">
            <draggable v-model="localColumns" handle=".drag-handle">
              <div
                v-for="column in localColumns"
                :key="column.key"
                class="export-list-item"
              >
                <div class="d-flex align-items-center text-truncate">
                  <span class="drag-handle text-dark mr-3">
                    <v-icon name="buttons/dots" class="text-dark" :size="12"/>
                  </span>
                  <span class="text-small">{{ column.label }}</span>
                </div>

                <div class="ant-switch-wrapper">
                  <b-form-checkbox v-model="column.visible" switch class="m-0" />

                  <div class="switch-icons-overlay">
                    <v-icon v-show="column.visible" name="buttons/check" class="ml-2" :size="11" style="color: #fff" />
                    <span v-show="!column.visible" class="flex-grow-1"></span>
                    <v-icon v-show="!column.visible" name="buttons/cross" class="mr-2" :size="10" style="color: #fff" />
                  </div>
                </div>
              </div>
            </draggable>
          </div>

          <div class="export-sidebar-footer p-2 border-top">
            <div class="d-flex flex-wrap align-items-center" style="gap: 12px;">
              <b-button variant="link" class="text-small p-0" style="color: #F5222D !important; text-decoration: none !important;" @click="resetSettings">
                Сбросить
              </b-button>
              <b-button v-if="checkAllExport" class="text-small text-dark p-0" variant="link" style="text-decoration: none !important; color: #6c757d;" @click="checkAllBoxes(false)">
                <v-icon name="buttons/toggle_left" :size="15" class="mr-1 pt-1"/> Снять все
              </b-button>
              <b-button v-else variant="link" class="text-small text-dark p-0" style="text-decoration: none !important" @click="checkAllBoxes(true)">
                <v-icon name="buttons/toggle_right" :size="15" class="mr-1 pt-1"/> Выбрать все
              </b-button>
              <slot name="extra-buttons"></slot>
              <b-button variant="link" class="text-small text-dark p-0" style="text-decoration: none !important" @click="saveExportSettings()">
                <v-icon name="buttons/save" :size="15" class="mr-1 pt-1"/> Сохранить настройки
              </b-button>
              <b-button class="custom-btn-tag text-small" :disabled="isExporting" @click="exportFile">
                <b-spinner v-if="isExporting" small class="mr-1" />
                <v-icon v-else name="buttons/export" :size="13" class="mr-1"/>
                Экспортировать
              </b-button>
            </div>
          </div>
        </div>
      </b-sidebar>
    </portal>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import draggable from "vuedraggable";

export default {
  name: "ExportSidebar",
  components: { draggable },
  props: {
    sidebarId: { type: String, required: true },
    storageKey: { type: String, required: true },
    storageVersion: { type: Number, default: 1 },
    columns: { type: Array, required: true },
    dataToExport: { type: Array, required: false, default: () => [] },
    dataProvider: { type: Function, required: false, default: null },
    dataMapper: { type: Function, required: true },
    fileName: { type: String, required: true },
    sheetName: { type: String, default: "Лист-1" },
    isBusy: { type: Boolean, default: false },
    itemsCount: { type: Number, default: 0 }
  },
  data() {
    return {
      isExporting: false,
      localColumns: [],
      checkAllExport: true,
    };
  },
  created() {
    this.initColumns();
  },
  methods: {
    initColumns() {
      const storedSettings = this.getStoredSettings();
      this.localColumns = this.mergeColumns(this.columns, storedSettings);
      this.updateCheckAllFlag();
    },
    checkAllBoxes(flag) {
      this.localColumns.forEach(column => column.visible = flag);
    },
    updateCheckAllFlag() {
      if (this.localColumns.length === 0) {
        this.checkAllExport = false;
        return;
      }

      const selectedCount = this.localColumns.filter(c => c.visible).length;
      this.checkAllExport = selectedCount === this.localColumns.length;
    },
    mergeColumns(incomingColumns, storedSettings) {
      if (!storedSettings) {
        return JSON.parse(JSON.stringify(incomingColumns));
      }

      const incomingMap = new Map(incomingColumns.map(c => [c.key, c]));
      const processedKeys = new Set();

      const merged = storedSettings.map(storedCol => {
        const incomingCol = incomingMap.get(storedCol.key);

        if (incomingCol) {
          processedKeys.add(storedCol.key);
          return { ...incomingCol, visible: storedCol.visible };
        }

        return null;
      }).filter(Boolean);

      incomingColumns.forEach(incomingCol => {
        if (!processedKeys.has(incomingCol.key)) {
          merged.push(incomingCol);
        }
      });

      return merged;
    },
    getStoredSettings() {
      const storedSettings = localStorage.getItem(this.storageKey);

      if (!storedSettings) {
        return [];
      }

      const parsedSettings = JSON.parse(storedSettings);

      if (Array.isArray(parsedSettings) || parsedSettings.version !== this.storageVersion) {
        localStorage.removeItem(this.storageKey);
        return [];
      }

      return parsedSettings.data ? parsedSettings.data : [];
    },
    saveExportSettings() {
      const settings = {
        version: this.storageVersion,
        data: this.localColumns
      };

      localStorage.setItem(this.storageKey, JSON.stringify(settings));
      this.$bvToast.toast("Настройки экспорта сохранены.", {
        title: 'Сохранено', variant: 'success', solid: true, autoHideDelay: 3000
      });
    },
    async exportFile() {
      const selectedColumns = this.localColumns.filter(c => c.visible);

      if (selectedColumns.length === 0) {
        this.$bvToast.toast("Выберите данные для экспорта.", { title: 'Ошибка', variant: 'danger' });
        return;
      }

      this.isExporting = true;
      const itemsToExport = this.dataProvider ? await this.dataProvider(selectedColumns) : this.dataToExport;

      if (!itemsToExport) {
        this.$bvToast.toast("Данные для экспорта не найдены.", { title: 'Ошибка', variant: 'danger' });
        return;
      }

      const mappedItems = itemsToExport.map(item => this.dataMapper(item));
      const dataForSheet = mappedItems.map(item => {
        const row = {};
        selectedColumns.forEach(column => {
          row[column.label] = item[column.key];
        });
        return row;
      });

      const worksheet = XLSX.utils.json_to_sheet(dataForSheet);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, this.sheetName);
      XLSX.writeFile(workbook, this.fileName);
      this.isExporting = false;
    },
    async resetSettings() {
      this.$root.$emit('bv::toggle::collapse', this.sidebarId);
      const isConfirmed = await this.$bvModal.msgBoxConfirm('Вы хотите сбросить настройки экспорта? (Это также обновит текущую страницу).', {
        title: 'Сброс настроек',
        size: 'md',
        buttonSize: 'sm',
        headerBgVariant: 'danger',
        okVariant: 'danger',
        okTitle: 'Сбросить',
        cancelTitle: 'Отмена',
        footerClass: 'p-1',
        centered: true
      });

      if (!isConfirmed) return;

      localStorage.removeItem(this.storageKey);
      this.initColumns();

      this.$bvToast.toast("Настройки экспорта сброшены.", {
        title: 'Успешно',
        variant: 'warning',
        autoHideDelay: 2000,
        solid: true
      });

      setTimeout(() => {
        location.reload();
      }, 1111);
    }
  },
  watch: {
    localColumns: {
      handler() {
        this.updateCheckAllFlag();
      }, deep: true
    },
    columns: {
      handler() {
        this.initColumns();
      }, deep: true
    }
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/utilites/commonUi/ExportSidebar.vue